import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { messages } from '@nesc/shared';
import { api, errorMessage } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input, Select, Textarea } from '@/components/ui/input';

/** メモ・次回アクションの登録 */
export const ActivityDialog = ({
  open,
  onOpenChange,
  companyId,
  defaultType = 'note',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  defaultType?: 'note' | 'next_action' | 'call';
}) => {
  const queryClient = useQueryClient();
  const [type, setType] = useState(defaultType);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [dueAt, setDueAt] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/companies/${companyId}/activities`, {
        type,
        title: title || undefined,
        body: body || undefined,
        dueAt: dueAt ? new Date(dueAt).toISOString() : undefined,
      }),
    onSuccess: () => {
      toast.success('登録しました');
      void queryClient.invalidateQueries({ queryKey: qk.companyTimeline(companyId) });
      void queryClient.invalidateQueries({ queryKey: ['activities'] });
      onOpenChange(false);
      setTitle('');
      setBody('');
      setDueAt('');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const dueRequired = type === 'next_action';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{dueRequired ? messages.activity.create : 'メモを追加する'}</DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-3">
          <Field label="種別" required>
            {(props) => (
              <Select {...props} value={type} onChange={(e) => setType(e.target.value as typeof type)}>
                <option value="note">メモ</option>
                <option value="call">架電</option>
                <option value="next_action">次回アクション</option>
              </Select>
            )}
          </Field>
          <Field label="内容" required>
            {(props) => <Input {...props} value={title} onChange={(e) => setTitle(e.target.value)} />}
          </Field>
          <Field label="詳細">
            {(props) => (
              <Textarea {...props} rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
            )}
          </Field>
          {dueRequired && (
            <Field label={messages.activity.changeDue.replace('する', '')} required>
              {(props) => (
                <Input
                  {...props}
                  type="datetime-local"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                />
              )}
            </Field>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button
            size="lg"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !title.trim() || (dueRequired && !dueAt)}
          >
            {messages.common.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
